import React from 'react';
import { useTranslate } from 'react-admin';

// Widget type icons
import BarChartIcon from '@mui/icons-material/BarChart';
import TableViewIcon from '@mui/icons-material/TableView';
import SpeedIcon from '@mui/icons-material/Speed';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import SchemaIcon from '@mui/icons-material/AccountTree';
import MapIcon from '@mui/icons-material/Map';

import { WidgetType } from 'types/dashboard.ui';

export interface WidgetTypeInfo {
  type: WidgetType;
  name: string;
  description: string;
  icon: React.ReactNode;
  defaultSize: { w: number; h: number };
}

/**
 * Hook для получения списка типов виджетов с локализованными названиями
 */
export const useWidgetTypes = (): WidgetTypeInfo[] => {
  const translate = useTranslate();
  
  return React.useMemo(() => [
    {
      type: 'chart',
      name: translate('resources.dashboards.widgets.chart.name'),
      description: translate('resources.dashboards.widgets.chart.description'),
      icon: <BarChartIcon />,
      defaultSize: { w: 6, h: 4 }
    },
    {
      type: 'table',
      name: translate('resources.dashboards.widgets.table.name'),
      description: translate('resources.dashboards.widgets.table.description'),
      icon: <TableViewIcon />,
      defaultSize: { w: 8, h: 5 }
    },
    {
      type: 'gauge',
      name: translate('resources.dashboards.widgets.gauge.name'),
      description: translate('resources.dashboards.widgets.gauge.description'),
      icon: <SpeedIcon />,
      defaultSize: { w: 4, h: 4 }
    },
    {
      type: 'text',
      name: translate('resources.dashboards.widgets.text.name'),
      description: translate('resources.dashboards.widgets.text.description'),
      icon: <TextFieldsIcon />,
      defaultSize: { w: 6, h: 3 }
    },
    {
      type: 'schema',
      name: translate('resources.dashboards.widgets.schema.name'),
      description: translate('resources.dashboards.widgets.schema.description'),
      icon: <SchemaIcon />,
      defaultSize: { w: 8, h: 6 }
    },
    {
      type: 'map',
      name: translate('resources.dashboards.widgets.map.name'),
      description: translate('resources.dashboards.widgets.map.description'),
      icon: <MapIcon />,
      defaultSize: { w: 12, h: 8 }
    }
  ], [translate]);
};

/**
 * Получает конфигурацию по умолчанию для указанного типа виджета
 */
export const getDefaultWidgetConfig = (type: WidgetType, translate: (key: string) => string): any => {
  switch (type) {
    case 'chart':
      return {
        chartType: 'line',
        dataSource: { items: [] },
        xAxis: '',
        yAxis: '',
        colors: ['#1976d2', '#388e3c', '#f57c00']
      };
      
    case 'table':
      return {
        dataSource: { items: [] },
        columns: [],
        pageSize: 25,
        showPagination: true,
        sortable: true,
        filterable: true,
        refreshInterval: 30000
      };
      
    case 'gauge':
      return {
        dataSource: { deviceId: '', archiveTypeId: '', paramId: '' },
        minValue: 0,
        maxValue: 100,
        thresholds: [
          { value: 70, color: '#ffc107', label: 'Предупреждение' },
          { value: 90, color: '#f44336', label: 'Критично' }
        ],
        refreshInterval: 5000
      };
      
    case 'text':
      return {
        content: translate('resources.dashboards.widget_wizard.default_text_content'),
        fontSize: 14,
        fontWeight: 'normal',
        color: '#000000',
        alignment: 'left'
      };
      
    case 'schema':
      return {
        dataSource: { items: [] }
      };
      
    case 'map':
      return {
        dataSource: { items: [] },
        center: { lat: 55.7558, lng: 37.6173 },
        zoom: 10
      };
      
    default:
      return {};
  }
};

/**
 * Находит информацию о типе виджета по его идентификатору
 */
export const findWidgetTypeInfo = (
  widgetTypes: WidgetTypeInfo[], 
  type: WidgetType | undefined
): WidgetTypeInfo | undefined => {
  return widgetTypes.find(t => t.type === type);
};
